import { Dispatch, SetStateAction, useRef, useState } from "react";
import { CustomDialogContent } from "../ui/custom-dialog";
import { Dialog, DialogTitle, DialogDescription } from "@radix-ui/react-dialog";
import { ArtmatchLoadImage } from "./ArtmatchLoadImage";
import { Tabs, TabsContent } from "../ui/tabs";
import { ArtmatchCropImage } from "./ArtmatchCropImage";
import { useTranslation } from "react-i18next";
import { ArtmatchResults } from "./ArtmatchResults";
import { ArtmatchResult, useArtmatchResults } from "@/api/artmatch";
import { ArtmatchCreateNewArtwork } from "./ArtmatchCreateNewArtwork";

const artmatchSteps = [
  "loadImage",
  "cropImage",
  "results",
  "createNewArtwork",
] as const;
type ArtmatchStep = (typeof artmatchSteps)[number];

export const ArtmatchIdentifyArtworkJourney = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation();
  const {
    mutateAsync: artmatchResults,
    isPending,
    isError,
  } = useArtmatchResults();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [results, setResults] = useState<ArtmatchResult[]>([]);

  const fileToUpload = useRef<File>();

  function goBack(index?: number) {
    if (index !== undefined) {
      setCurrentStepIndex(index);
      return;
    }
    if (currentStepIndex === 0) {
      onOpenChange(false);
      return;
    }
    setCurrentStepIndex(currentStepIndex - 1);
  }

  async function handleGetArtmatchResults() {
    if (!fileToUpload.current) return;
    setResults([]);
    try {
      const res = await artmatchResults(fileToUpload.current);
      setResults(res);
    } catch {
      setResults([]);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <CustomDialogContent className="min-w-fit min-h-fit max-w-fit max-h-fit duration-100 ease-in-out">
        <DialogTitle className="sr-only">{t("artmatch.title")}</DialogTitle>
        <DialogDescription className="sr-only">
          {t("artmatch.description")}
        </DialogDescription>
        <Tabs
          value={artmatchSteps[currentStepIndex]}
          onValueChange={(v) =>
            setCurrentStepIndex(artmatchSteps.indexOf(v as ArtmatchStep))
          }
        >
          <TabsContent value={artmatchSteps[0]}>
            <ArtmatchLoadImage
              onNextStep={(file) => {
                fileToUpload.current = file;
                setCurrentStepIndex(1);
              }}
              onCancel={() => goBack()}
            />
          </TabsContent>
          <TabsContent value={artmatchSteps[1]}>
            {fileToUpload.current && (
              <ArtmatchCropImage
                baseImage={fileToUpload.current}
                onNextStep={(file) => {
                  fileToUpload.current = file;
                  handleGetArtmatchResults();
                  setCurrentStepIndex(2);
                }}
                onCancel={() => goBack()}
              />
            )}
          </TabsContent>
          {fileToUpload.current && (
            <TabsContent value={artmatchSteps[2]}>
              <ArtmatchResults
                baseImage={fileToUpload.current}
                results={results}
                isPending={isPending}
                onNextStep={() => {
                  setCurrentStepIndex(3);
                }}
                onReset={() => goBack(0)}
                isError={isError}
              />
            </TabsContent>
          )}
          {fileToUpload.current && (
            <TabsContent value={artmatchSteps[3]}>
              <ArtmatchCreateNewArtwork
                image={fileToUpload.current}
                onCancel={() => goBack()}
              />
            </TabsContent>
          )}
        </Tabs>
      </CustomDialogContent>
    </Dialog>
  );
};
